import React, { useState, useEffect } from 'react';
import { useFlight } from '../contexts/FlightContext';
import { database } from '../services/firebase';
import { ref, onValue } from 'firebase/database';
import { PaperAirplaneIcon, ArrowLeftOnRectangleIcon, PlusCircleIcon, RocketLaunchIcon } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import { UserRole, STATIONS } from '../types';

const NewFlightModal: React.FC<{ onClose: () => void; }> = ({ onClose }) => {
    const { startNewFlightSession } = useFlight();
    const [flightNumbers, setFlightNumbers] = useState<string[]>([]);
    const [selectedFlight, setSelectedFlight] = useState<string>('');
    const [flightDate, setFlightDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const flightNumbersRef = ref(database, 'config/flightNumbers');
        const unsubscribe = onValue(flightNumbersRef, (snapshot) => {
            const data = snapshot.val() || [];
            setFlightNumbers(data);
            if (data.length > 0 && !selectedFlight) {
                setSelectedFlight(data[0]);
            }
        });
        return () => unsubscribe();
    }, [selectedFlight]);

    const handleStartReport = async () => {
        if (!selectedFlight || !flightDate) {
            setError('Please select a flight number and date.');
            return;
        }
        setError('');
        setIsLoading(true);
        try {
            await startNewFlightSession(selectedFlight, flightDate);
            // On success, the main app will switch to the main layout automatically
            onClose();
        } catch (e) {
            setError('Failed to start new report. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md border border-slate-700">
                <h3 className="text-xl font-semibold text-white mb-4">Start New Flight Report</h3>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="flightNoSelect" className="block text-sm font-medium text-slate-300 mb-2">Flight Number</label>
                        <select
                            id="flightNoSelect"
                            value={selectedFlight}
                            onChange={(e) => setSelectedFlight(e.target.value)}
                            className="w-full bg-slate-700 border border-slate-600 rounded-md py-2 px-3 text-white"
                        >
                            <option value="" disabled>Select a flight</option>
                            {flightNumbers.map(fn => <option key={fn} value={fn}>{fn}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="flightDate" className="block text-sm font-medium text-slate-300 mb-2">Flight Date</label>
                        <input
                            type="date"
                            id="flightDate"
                            value={flightDate}
                            onChange={(e) => setFlightDate(e.target.value)}
                            className="w-full bg-slate-700 border border-slate-600 rounded-md py-2 px-3 text-white"
                        />
                    </div>
                     {error && <p className="text-red-400 text-sm text-center pt-2">{error}</p>}
                </div>
                <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-slate-700">
                    <button onClick={onClose} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded-lg">Cancel</button>
                    <button onClick={handleStartReport} disabled={isLoading} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-slate-500">
                        {isLoading ? 'Starting...' : 'Start Report'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const FlightSelectionScreen: React.FC = () => {
  const { activeFlights, selectFlight, triggerNewFlightModal, clearNewFlightModalTrigger } = useFlight();
  const { user, logout, isMultiFlightEnabled } = useAuth();
  const [showNewFlightModal, setShowNewFlightModal] = useState(false);

  useEffect(() => {
    if (triggerNewFlightModal) {
      setShowNewFlightModal(true);
      clearNewFlightModalTrigger();
    }
  }, [triggerNewFlightModal, clearNewFlightModalTrigger]);

  const MAX_FLIGHTS = isMultiFlightEnabled ? 3 : 1;
  const canAddFlight = user && [UserRole.ADMIN, UserRole.SUPERVISOR].includes(user.role) && Object.keys(activeFlights).length < MAX_FLIGHTS;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 p-4">
      <div className="absolute top-4 right-4">
        <button
          onClick={logout}
          className="text-slate-400 hover:text-white p-1.5 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-blue-500"
          title="Logout"
          aria-label="Logout"
        >
          <ArrowLeftOnRectangleIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="max-w-4xl w-full mx-auto text-center">
        <PaperAirplaneIcon className="mx-auto h-12 w-auto text-blue-500 -rotate-45" />
        <h2 className="mt-6 text-3xl font-extrabold text-slate-100">
          Active Flights Dashboard
        </h2>
        <p className="mt-2 text-md text-slate-400">
          Select an active flight or start a new one.
        </p>

        <div className="mt-10 flex flex-wrap -m-3">
          {Object.entries(activeFlights).map(([slotId, flightData]) => {
              const lastCompletedIndex = STATIONS.slice().reverse().findIndex(station => flightData.crewMovement[station]?.crew.length > 0 || flightData.crewMovement[station]?.pilots.length > 0);
              const progress = lastCompletedIndex !== -1 ? ((STATIONS.length - lastCompletedIndex) / STATIONS.length) * 100 : 0;

              return (
                 <div key={slotId} className="w-full md:w-1/2 lg:w-1/3 p-3">
                    <button onClick={() => selectFlight(slotId)} className="w-full h-full bg-slate-800/50 text-left border border-slate-700 rounded-lg p-5 hover:bg-slate-800/80 hover:border-blue-500 transition-all duration-200 group">
                        <div className="flex justify-between items-center">
                            <span className="text-xl font-bold text-white group-hover:text-blue-400">{flightData.counterDistribution.flightNo}</span>
                            <span className="text-sm font-mono bg-slate-700 text-slate-300 px-2 py-1 rounded">{flightData.counterDistribution.etd ? new Date(flightData.counterDistribution.etd).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}</span>
                        </div>
                        <div className="text-sm text-slate-400 mt-2">
                            ETD: {flightData.counterDistribution.etd ? new Date(flightData.counterDistribution.etd).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit'}) : 'N/A'}
                        </div>
                        <div className="mt-4">
                            <div className="w-full bg-slate-700 rounded-full h-2.5">
                                <div className="bg-blue-600 h-2.5 rounded-full group-hover:bg-blue-500" style={{ width: `${progress}%` }}></div>
                            </div>
                            <p className="text-xs text-slate-500 text-right mt-1">{Math.round(progress)}% Complete</p>
                        </div>
                        <div className="mt-4 pt-4 border-t border-slate-700 text-center">
                            <span className="font-semibold text-lg text-blue-400 group-hover:text-white flex items-center justify-center gap-2">
                                <RocketLaunchIcon className="w-5 h-5"/>
                                Manage Flight
                            </span>
                        </div>
                    </button>
                 </div>
              )
          })}
          
          {canAddFlight && (
              <div className="w-full md:w-1/2 lg:w-1/3 p-3">
                <button onClick={() => setShowNewFlightModal(true)} className="w-full h-full border-2 border-dashed border-slate-600 rounded-lg p-5 flex flex-col items-center justify-center text-slate-500 hover:bg-slate-800 hover:text-green-400 hover:border-green-500 transition-all duration-200">
                    <PlusCircleIcon className="w-16 h-16"/>
                    <span className="mt-2 text-xl font-bold">Add New Flight</span>
                    <span className="text-sm">{MAX_FLIGHTS - Object.keys(activeFlights).length} slot(s) available</span>
                </button>
              </div>
          )}

        </div>
      </div>
      
      {showNewFlightModal && <NewFlightModal onClose={() => setShowNewFlightModal(false)} />}
      
      <div className="text-center mt-12 text-xs text-slate-500">
        Crew-movement Developed By John tawfik One Of EAS Team Members
      </div>
    </div>
  );
};

export default FlightSelectionScreen;